import React, { useState } from 'react';
import { API_BASE } from '../utils/apiEndpoints';
import { LIMITS, validateRegistrationEmailDomain } from '../utils/validation.ts';

type Props = {
  currentEmail: string;
  pendingEmail: string | null;
  onPendingChange: (pending: string | null) => void;
  t: (key: string) => string;
  currentLanguage: string;
};

function translateServerError(t: (key: string) => string, raw: string): string {
  if (raw.startsWith('settings.') || raw.startsWith('registration.') || raw.startsWith('verifyEmail.')) {
    const translated = t(raw);
    return translated === raw ? raw : translated;
  }
  return raw;
}

export function ChangeEmailSettings({
  currentEmail,
  pendingEmail,
  onPendingChange,
  t,
  currentLanguage,
}: Props) {
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  const applySuccess = (pending: string | null, link?: string) => {
    onPendingChange(pending);
    setNewEmail('');
    setDevLink(link || null);
    setSuccess(
      pending
        ? t('settings.emailChangeSent').replace('{{email}}', pending)
        : t('settings.emailChangeCancelled')
    );
  };

  const requestChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setDevLink(null);

    const domainErr = validateRegistrationEmailDomain(newEmail);
    if (domainErr) {
      setError(domainErr.startsWith('registration.') ? t(domainErr) : t('settings.emailInvalid'));
      return;
    }
    if (newEmail.trim().toLowerCase() === currentEmail.trim().toLowerCase()) {
      setError(t('settings.emailSame'));
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_BASE}/api/profile/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: newEmail.trim(), lang: currentLanguage }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(translateServerError(t, data.error || data.message || t('settings.emailChangeFailed')));
        return;
      }
      applySuccess(
        typeof data.pending_email === 'string' ? data.pending_email : newEmail.trim().toLowerCase(),
        typeof data.devVerificationLink === 'string' ? data.devVerificationLink : undefined
      );
    } catch {
      setError(t('settings.emailChangeFailed'));
    } finally {
      setSaving(false);
    }
  };

  const resend = async () => {
    setError(null);
    setSuccess(null);
    setDevLink(null);
    try {
      setResending(true);
      const response = await fetch(`${API_BASE}/api/profile/email/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lang: currentLanguage }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(translateServerError(t, data.error || data.message || t('settings.emailChangeFailed')));
        return;
      }
      applySuccess(
        typeof data.pending_email === 'string' ? data.pending_email : pendingEmail,
        typeof data.devVerificationLink === 'string' ? data.devVerificationLink : undefined
      );
    } catch {
      setError(t('settings.emailChangeFailed'));
    } finally {
      setResending(false);
    }
  };

  const cancelPending = async () => {
    setError(null);
    setSuccess(null);
    setDevLink(null);
    try {
      setCancelling(true);
      const response = await fetch(`${API_BASE}/api/profile/email/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(translateServerError(t, data.error || data.message || t('settings.emailChangeFailed')));
        return;
      }
      applySuccess(null);
    } catch {
      setError(t('settings.emailChangeFailed'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <form onSubmit={requestChange} className="settings-form settings-password-form" autoComplete="off">
      <div className="settings-section">
        <h2 className="section-title">{t('settings.emailTitle')}</h2>
        <p className="settings-password-intro">{t('settings.emailIntro')}</p>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        {devLink && (
          <p className="settings-email-dev-link">
            {t('registration.devLinkHint')}{' '}
            <a href={devLink}>{devLink}</a>
          </p>
        )}
        {pendingEmail && (
          <p className="settings-email-pending">
            {t('settings.emailPendingHint').replace('{{email}}', pendingEmail)}
          </p>
        )}
        <div className="settings-grid-3">
          <div className="settings-field">
            <label className="settings-field-label" htmlFor="current-email">
              {t('settings.emailCurrent')}
            </label>
            <input
              id="current-email"
              type="email"
              className="settings-input"
              value={currentEmail}
              disabled
              readOnly
            />
          </div>
          <div className="settings-field">
            <label className="settings-field-label" htmlFor="new-email">
              {t('settings.emailNew')}
            </label>
            <input
              id="new-email"
              type="email"
              className="settings-input"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder={t('settings.emailNewPlaceholder')}
              autoComplete="email"
              maxLength={LIMITS.EMAIL_MAX}
            />
          </div>
        </div>
        <div className="form-actions settings-password-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || !newEmail.trim()}>
            {saving ? t('settings.saving') : t('settings.emailSave')}
          </button>
          {pendingEmail && (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resend}
                disabled={resending || cancelling}
              >
                {resending ? t('settings.saving') : t('settings.emailResend')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelPending}
                disabled={resending || cancelling}
              >
                {cancelling ? t('settings.saving') : t('settings.emailCancel')}
              </button>
            </>
          )}
        </div>
      </div>
    </form>
  );
}
